/**
 * Bake / Simplify sheet (W2-11 batch-tools; DESIGN.md §5.6.5, §5.8; RPE Cut EVT-15, E§4.6; Fit UNDOC-2).
 *  - Bake: the selected segments become straight pieces every (1/snap)/density beat, anchored on the grid line
 *    nearest the playhead (or from each segment's start), values sampled from the exact curve. X/Y-linked partners
 *    are always baked; on other lines the other position axis can be baked over the same span too.
 *  - Simplify: dense keys merge into fewer eased segments within a tolerance (`Tools.fit`), the inverse of Bake.
 * Live ghosts through `useToolGesture`. Each mode shows its formula (key beats and values, or the fit condition).
 */

import { useMemo, useState } from 'react';
import type { DialogProps } from '../../../app/registry/types';
import { Core } from '../../../core/commands/index';
import type { KeyId } from '../../../core/model/types';
import type { TxBuilder } from '../../../core/ops/types';
import { formatBeat } from '../../../core/time/beatSyntax';
import { numText } from '../../../core/tools/common';
import { Tools } from '../../../core/tools/index';
import type { ToolReport } from '../../../core/tools/types';
import { Checkbox } from '../../../ui/Checkbox';
import { EmptyState } from '../../../ui/EmptyState';
import { Segmented } from '../../../ui/Segmented';
import { m } from '../batch.messages';
import s from '../Batch.module.css';
import {
  DEFAULT_BAKE_DENSITY,
  EXT,
  extNumber,
  playheadBeat,
  ref,
  remember,
  snapDiv,
  toastNotes,
} from '../batchContext';
import { bakeDiv, hasUnlinkedPartner, partnerSegments } from '../segmentOps';
import { ExpressionBox, NumberInput, ToolSheet, useText, type SheetStatus } from '../sheetKit';
import { useToolGesture } from '../useToolGesture';

export type BakeMode = 'bake' | 'simplify';

type Result =
  { readonly kind: 'bake'; readonly pieces: number } | { readonly kind: 'simplify'; readonly report: ToolReport };

export function BakeSheet(p: DialogProps<'bake'>) {
  const { services, props } = p;
  const text = useText();
  const store = services.store;
  const segments = useMemo(
    () =>
      props.segments.filter((id) => store !== null && store.doc.keys.has(id) && store.idx.nextKey(id) !== undefined),
    [props.segments, store],
  );
  // The opener's mode (DialogMap.bake `mode`, CR-W2-11-3), e.g. `tools.bake { mode: 'simplify' }`.
  const [mode, setMode] = useState<BakeMode>(() => props.mode ?? 'bake');
  const [density, setDensity] = useState(() => extNumber(services, EXT.bakeDensity, DEFAULT_BAKE_DENSITY, 1, 64));
  const [anchor, setAnchor] = useState<'playhead' | 'start'>('playhead');
  const [partner, setPartner] = useState(false);
  const [tolerance, setTolerance] = useState(() => extNumber(services, EXT.simplifyTolerance, 1, 0.0001, 10000));
  const [anchorBeat] = useState(() => playheadBeat(services));
  const showPartner = useMemo(() => store !== null && hasUnlinkedPartner(store, segments), [store, segments]);
  const div = bakeDiv(snapDiv(services), density);

  const build = useMemo(() => {
    if (segments.length === 0) return null;
    if (mode === 'simplify') {
      return (tx: TxBuilder): Result => ({ kind: 'simplify', report: Tools.fit(tx, { segments, tolerance }) });
    }
    return (tx: TxBuilder): Result => {
      const all: KeyId[] = partner ? [...segments, ...partnerSegments(tx, segments)] : segments;
      const r = Core.keys.bakeSegments(tx, {
        segments: all,
        div,
        ...(anchor === 'playhead' ? { anchor: anchorBeat } : {}),
      });
      return { kind: 'bake', pieces: r.pieces };
    };
  }, [segments, mode, tolerance, partner, div, anchor, anchorBeat]);
  const gesture = useToolGesture<Result>({
    services,
    label: ref(mode === 'bake' ? m.txnBake : m.txnSimplify),
    command: 'tools.bake',
    build,
  });

  const cancel = (): void => {
    gesture.cancel();
    p.close(null);
  };
  if (segments.length === 0) {
    return (
      <ToolSheet
        title={text.t(m.bakeTitle)}
        status={{ kind: 'none' }}
        applyLabel={text.t(m.apply)}
        applyDisabledReason={ref(m.reasonNoSegments)}
        onApply={() => undefined}
        onCancel={cancel}
      >
        <EmptyState icon="Scissors" title={ref(m.reasonNoSegments)} body={ref(m.bakeHowTo)} />
      </ToolSheet>
    );
  }

  const outcome = gesture.outcome;
  let status: SheetStatus = { kind: 'none' };
  if (gesture.status === 'paused') status = { kind: 'warn', text: text.t(m.previewPaused) };
  else if (outcome !== null && !outcome.ok) status = { kind: 'error', text: text.tr(outcome.reason) };
  else if (outcome?.ok === true) {
    const v = outcome.value;
    status =
      v.kind === 'bake'
        ? { kind: 'ok', text: text.t(m.bakeStatus, { count: v.pieces }) }
        : {
            kind: 'ok',
            text: text.t(m.simplifyStatus, {
              removed: v.report.changed,
              error: Number((v.report.maxError ?? 0).toPrecision(3)),
            }),
          };
  }

  const apply = (): void => {
    const out = gesture.apply();
    if (out === null || !out.ok) return;
    if (mode === 'bake') remember(services, EXT.bakeDensity, density);
    else remember(services, EXT.simplifyTolerance, tolerance);
    const v = out.value;
    services.ui.toast(
      v.kind === 'bake' ? ref(m.bakeDone, { count: v.pieces }) : ref(m.simplifyDone, { count: v.report.changed }),
      { kind: 'success', undo: out.txn !== null },
    );
    toastNotes(services, out.notes);
    p.close({ applied: true });
  };

  return (
    <ToolSheet
      title={text.t(m.bakeTitle)}
      subtitle={text.t(m.countSegments, { count: segments.length })}
      status={status}
      applyLabel={text.t(m.apply)}
      onApply={apply}
      onCancel={cancel}
    >
      <Segmented<BakeMode>
        label={text.t(m.bakeMode)}
        value={mode}
        options={[
          { value: 'bake', label: text.t(m.bakeModeBake) },
          { value: 'simplify', label: text.t(m.bakeModeSimplify) },
        ]}
        onChange={setMode}
      />
      {mode === 'bake' ? (
        <>
          <NumberInput
            label={text.t(m.bakeDensity)}
            value={density}
            integer
            min={1}
            max={64}
            onChange={setDensity}
            help={ref(m.bakeDensityHelp, { div })}
          />
          <Segmented<'playhead' | 'start'>
            label={text.t(m.bakeAnchor)}
            value={anchor}
            options={[
              { value: 'playhead', label: text.t(m.bakeAnchorPlayhead) },
              { value: 'start', label: text.t(m.bakeAnchorStart) },
            ]}
            onChange={setAnchor}
          />
          {showPartner && (
            <Checkbox className={s.check} label={text.t(m.bakePartner)} checked={partner} onChange={setPartner} />
          )}
          <ExpressionBox
            services={services}
            label={text.t(m.equivalentExpression)}
            code={`t = ${anchor === 'playhead' ? formatBeat(anchorBeat) : text.t(m.bakeFormulaStart)} + k / ${String(div)}\nv = curve(t)`}
            note={text.t(m.bakeFormulaNote, { div })}
          />
          <p className={s.hint}>{text.t(m.bakeHint)}</p>
        </>
      ) : (
        <>
          <NumberInput
            label={text.t(m.tolerance)}
            value={tolerance}
            min={0.0001}
            max={10000}
            step={0.1}
            onChange={setTolerance}
          />
          <ExpressionBox
            services={services}
            label={text.t(m.equivalentExpression)}
            code={`|fit(t) - curve(t)| <= ${numText(tolerance)}`}
            note={text.t(m.simplifyFormulaNote)}
          />
          <p className={s.hint}>{text.t(m.simplifyHint)}</p>
        </>
      )}
    </ToolSheet>
  );
}
